import { useEffect, useRef, useState } from "react";

import {
  Button,
  Drawer,
  Label,
  Loading,
  Steps as StepsComponent,
} from "@components";

import { CreativeName } from "./components/CreativeName";
import { PushMessage } from "./components/PushMessage";

import CreativeDetails from "@containers/flows/CreativeDetails";
import { FlowAPI } from "@providers/FlowAPI";
import { FlowStorage } from "@storage/FlowStorage";
import styles from "@styles/forms-flow/creative.module.scss";
import { ContemplationInterval } from "./components/ContemplationInterval";
import { Environment } from "shared/core/Environment";
import { If } from "shared/components/control-statements";
import Coupons from "./components/Coupons";

const options = {
  REPORT: "REPORT",
  FORM: "FORM",
};

let Steps = {
  NAME: 1,
  CONTEMPLATION: 2,
  CHOOSE_BENEFIT: 3,
  PUSH_MESSAGE: 4,
  FINISHED: 5,
};

export const PushCreativeActionForm = ({
  visible,
  onClose,
  node,
  restrictions,
  flowType,
  onChange,
  onSave,
  boxes,
  isCreativeAction,
}) => {
  const [steps] = useState(Object.keys(Steps).length);
  const [currentStep, setCurrentStep] = useState(Steps.NAME);
  const [creative, setCreative] = useState({
    creative: node?.data?.creative || {},
    coupon: node?.data?.coupon || {},
  });
  const [isValid, setIsValid] = useState(false);
  const [brandsList, setBrands] = useState([]);
  const [showStartOptions, setShowStartOptions] = useState(false);
  const [step, setStep] = useState(null);
  const [report, setReport] = useState({});
  const [maxGainEnabled, setMaxGainEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const isBroadcastFlow = (boxes || []).some(
    (box) => box.type === "BroadcastAudience"
  );

  const getFreshNode = (data) => {
    return {
      ...node,
      data: {
        ...node.data,
        ...data,
      },
    };
  };

  useEffect(() => {
    const c = {
      creative: node?.data?.creative || {},
      coupon: node?.data?.coupon || {},
    };
    setCreative(c);
    setIsValid(isValidStep(currentStep, c, flowType, maxGainEnabled));
  }, [node]);

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, [currentStep]);

  const onChangeCreative = (branch, value) => {
    const newCreative = {
      ...creative,
      [branch]: {
        ...(creative[branch] || {}),
        ...value,
      },
    };

    setCreative(newCreative);
    setIsValid(isValidStep(currentStep, newCreative, flowType, maxGainEnabled));
    onChange && onChange(getFreshNode(newCreative));
    return getFreshNode(newCreative);
  };

  useEffect(() => {
    setIsValid(isValidStep(currentStep, creative, flowType, maxGainEnabled));
  }, []);

  useEffect(() => {
    if (isCreativeAction) setShowStartOptions(true);
  }, [isCreativeAction]);

  const nextStep = (futureCreative) => {
    let nextStep = getNextStep(currentStep, isBroadcastFlow);

    onSave && onSave(getFreshNode(futureCreative || creative), false);

    return nextStep;
  };

  const loadReport = async (flowId, stepId, creativeId) => {
    setLoading(true);
    const response = await FlowAPI.getFlowReports(flowId, stepId);
    const allCreatives = response.getData({ creatives: [] }).creatives;
    const selectedCreative = allCreatives.filter((c) => c.id === creativeId);

    setReport(selectedCreative[0]);
    setLoading(false);
  };

  useEffect(() => {
    if (visible) {
      const stepId = node?.data?.stepId;
      const flowId = node?.data?.flow_id;
      const creativeId = node?.data?.creative?.id;

      loadReport(flowId, stepId, creativeId);
    }
  }, [node?.data?.stepId]);

  const creativeName = useRef(creative?.creative?.name);
  const startedCreative = creativeName.current;

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose();
        setCurrentStep(Steps.NAME);

        if (showStartOptions) setStep(null);
      }}
      showBackButton={
        (currentStep > Steps.NAME && currentStep != Steps.FINISHED) ||
        (step && showStartOptions)
      }
      backButton={() =>
        step && currentStep === Steps.NAME
          ? setStep(null)
          : setCurrentStep((prevState) =>
              getPrevStep(prevState, creative, null, isBroadcastFlow)
            )
      }
      title={"Adicionar um criativo - Push"}
    >
      {showStartOptions && !step && !!startedCreative && (
        <>
          <Label>O que deseja fazer?</Label>

          <div className={styles.options}>
            <div
              className={styles.option}
              onClick={() => setStep(options.REPORT)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-list" />
                </div>
                <p>Ver relatório</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
            <div
              className={styles.option}
              onClick={() => setStep(options.FORM)}
            >
              <div className="d-flex align-center">
                <div className={styles.icon}>
                  <i className="fal fa-pen-to-square" />
                </div>
                <p>Editar disparo</p>
              </div>
              <div className={styles.chevron}>
                <i className="fas fa-chevron-right" />
              </div>
            </div>
          </div>
        </>
      )}

      {step === options.REPORT && (
        <>
          <If cond={loading}>
            <div className={styles.loadingReports}>
              <Loading />
            </div>
          </If>

          <If cond={!loading}>
            <CreativeDetails
              flowType={FlowStorage.getFlowType(flowType)}
              creative={creative}
              node={node}
              report={report}
            />
          </If>
        </>
      )}

      {(((step === options.FORM || !showStartOptions) && startedCreative) ||
        !startedCreative) && (
        <>
          <StepsComponent steps={steps} currentStep={currentStep} />
          <div className={styles.scrollContainer}>
            {currentStep === Steps.NAME && (
              <CreativeName
                node={node}
                flowType={flowType}
                restrictions={restrictions}
                creative={creative?.creative}
                onChangeCreative={(data) => onChangeCreative("creative", data)}
              />
            )}

            {currentStep === Steps.CONTEMPLATION && (
              <ContemplationInterval
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.CHOOSE_BENEFIT && (
              <Coupons
                creative={creative?.creative}
                onChangeCreative={(data) => {
                  onChangeCreative("creative", data);
                }}
              />
            )}

            {currentStep === Steps.PUSH_MESSAGE && (
              <PushMessage
                coupon={creative.coupon}
                message={creative.creative?.message}
                senderName={creative?.creative?.senderName}
                url={creative?.creative?.url}
                onChangeCreative={(data) =>
                  onChangeCreative("creative", { message: data })
                }
              />
            )}

            {currentStep === Steps.FINISHED && (
              <div className={styles.finished}>
                <i className="far fa-check" />
                <p>Seu disparo foi configurado com sucesso!</p>
              </div>
            )}
          </div>

          <div className={styles.footer}>
            <div className="d-flex flex-column w-100">
              {currentStep !== Steps.FINISHED && (
                <>
                  <Button
                    className="w-100"
                    disabled={!isValid}
                    onClick={() => setCurrentStep(nextStep())}
                    design="primary"
                  >
                    Próximo
                  </Button>
                </>
              )}

              {currentStep === Steps.FINISHED && (
                <Button
                  className="w-100"
                  onClick={() => {
                    setCurrentStep(Steps.NAME);
                    setCreative({});
                    onClose();
                  }}
                >
                  Voltar para fluxos
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

const getNextStep = (currentStep, isBroadcastFlow) => {
  switch (currentStep) {
    case Steps.NAME:
      return Steps.CONTEMPLATION;
    case Steps.CONTEMPLATION:
      if (isBroadcastFlow) return Steps.PUSH_MESSAGE;
      return Steps.CHOOSE_BENEFIT;
    case Steps.CHOOSE_BENEFIT:
      return Steps.PUSH_MESSAGE;
    case Steps.PUSH_MESSAGE:
      return Steps.FINISHED;
    default:
      return currentStep;
  }
};

const getPrevStep = (
  currentStep,
  { creative, coupon },
  brandsList,
  isBroadcastFlow
) => {
  switch (currentStep) {
    case Steps.NAME:
      return null;
    case Steps.CONTEMPLATION:
      return Steps.NAME;
    case Steps.CHOOSE_BENEFIT:
      return Steps.CONTEMPLATION;
    case Steps.PUSH_MESSAGE:
      if (isBroadcastFlow) return Steps.CONTEMPLATION;
      return Steps.CHOOSE_BENEFIT;
    default:
      return currentStep;
  }
};

const replaceTags = (template, coupon) => {
  const message = template || "";
  let tagPairs = [];

  if (coupon?.code)
    tagPairs = [{ from: /\<cupom\>/gi, to: `${coupon?.code}-A10` }];

  return tagPairs?.reduce(
    (str, tag) => str?.replace(tag.from, tag.to),
    String(message)
  );
};

const isValidStep = (currentStep, data, flowType, maxGainEnabled) => {
  const { creative, coupon } = data;

  switch (currentStep) {
    case Steps.NAME:
      return !!creative?.name && !!(flowType == "EVENT" || creative?.sendTime);
    case Steps.CONTEMPLATION:
      return !!creative?.contemplation && !!creative?.contemplation?.type;
    case Steps.PUSH_MESSAGE:
      const isCocoBambu = Environment.id === 49;
      const finalMessage =
        replaceTags(creative?.message?.template || "", coupon) || "";

      if (isCocoBambu && finalMessage.length > 107) return false;
      if (!isCocoBambu && finalMessage.length > 160) return false;

      return !!creative?.message?.template && !!creative?.message?.title;
  }

  return true;
};
